import React, { useState, useEffect, useRef, useCallback } from 'react';
// eslint-disable-next-line yola/import/no-extraneous-dependencies
import {
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  offset,
  autoUpdate,
  arrow,
  safePolygon,
  shift,
  flip,
} from '@floating-ui/react';
import {
  PLACEMENTS,
  APPEARANCES,
  MODES,
  MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS,
  FLOATING_OFFSET,
} from './constants';
import PopoverContext from './context';
import { PopoverContent, PopoverTrigger } from './components';
const HIDE_EVENT_NAME = 'ws-popover:hide';

const hide = () => {
  document.dispatchEvent(new Event(HIDE_EVENT_NAME));
};

const Popover = ({
  children,
  hotkey = '',
  placement = PLACEMENTS.topCenter,
  title = '',
  description = null,
  // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number | undefined'.
  width = null,
  onHover = Function.prototype,
  disabled = false,
  appearance = APPEARANCES.light,
  mode = MODES.tooltip,
  imgSrc = '',
  // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number | undefined'.
  customOffset = null,
  isOpen = null,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isVisible,
    placement: MAP_PLACEMENTS_TO_FLOATING_UI_PLACEMENTS[placement],
    middleware: [
      offset(customOffset || FLOATING_OFFSET),
      flip({
        fallbackAxisSideDirection: 'start',
        crossAxis: false,
      }),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    onOpenChange: (isShowed, event) => {
      if (mode === MODES.popover && !isShowed && event.target.closest('.ws-ui-popover-content')) {
        return;
      }
      setIsVisible(isShowed);
    },
  });

  const hover = useHover(context, {
    enabled: mode === MODES.tooltip && !disabled,
    move: true,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: mode === MODES.tooltip && !disabled });
  const click = useClick(context, {
    enabled: mode === MODES.popover && !disabled && isOpen === null,
  });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss]);

  const hidePopover = useCallback(() => {
    if (disabled || !isVisible) {
      return;
    }

    setIsVisible(false);
  }, [disabled, isVisible]);

  const onMouseEnter = () => {
    if (disabled) {
      return;
    }

    onHover();
  };

  useEffect(() => {
    if (!isVisible) return () => {};

    document.addEventListener('scroll', hidePopover, true);
    document.addEventListener(HIDE_EVENT_NAME, hidePopover, false);

    return () => {
      document.removeEventListener('scroll', hidePopover, true);
      document.removeEventListener(HIDE_EVENT_NAME, hidePopover, false);
    };
  }, [isVisible, hidePopover]);

  return (
    <PopoverContext.Provider
      value={{
        isVisible: isOpen === null ? isVisible : isOpen,
        title,
        description,
        width,
        hotkey,
        appearance,
        imgSrc,
        refs,
        floatingStyles,
        arrowRef,
        context,
        getFloatingProps,
      }}
    >
      <div
        className="ws-ui-popover"
        ref={refs.setReference}
        {...getReferenceProps({ onMouseEnter })}
      >
        {children}
      </div>
    </PopoverContext.Provider>
  );
};

Popover.hide = hide;

export { Popover, PopoverTrigger, PopoverContent };
